import { zodResolver } from '@hookform/resolvers/zod'
import { cn } from '@repo/tailwind-config/tailwind.functions'
import { useNavigator } from '@repo/toolkit/use-navigator'
import Badge from '@repo/ui/badge'
import Button from '@repo/ui/button'
import { EyeOpenIcon, NotebookIcon } from '@repo/ui/icons'
import Loader from '@repo/ui/loader'
import Modal, { MODAL_BODY_CLASSNAME } from '@repo/ui/modal'
import LineTab from '@repo/ui/tabs'
import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useMemo, useState } from 'react'
import { useForm } from 'react-hook-form'

import { Paths } from '../../../../lib/constants/path'
import useTranslate from '../../../../lib/hooks/use-translate'
import {
  StaffApplicationStatusEnum,
  staffApplicationStatusEnumMapping
} from '../../../../lib/types/enums/staff-application/staff-application-status.enum'
import { ScoreCardInput } from '../../../../lib/types/models/score-card/score-card-input'
import { StaffApplicationLight } from '../../../../lib/types/models/staff-application/staff-application'
import { modalButtonConfig } from '../../../../lib/utils/buttons-config/validation-buttons-config'
import { FORM_MODE } from '../../../../utils/constants/form.constants'
import { Form } from '../../../elements/forms/form'
import ValidationButtons from '../../../elements/validation-buttons'
import {
  getScoreCardEnteredFieldsStatus,
  ScoreCardEnteredFieldsStatusEnum
} from './pcr-score-card.utils'
import {
  DateAndMethodOfContact,
  PcrScoreCardFormCandidateDetails
} from './pcr-score-card-form-candidate-details'
import PcrScoreCardFormEvaluation from './pcr-score-card-form-evaluation'
import {
  PcrScoreCardFormInterviewSchedule,
  PcrScoreCardFormInterviewTranscriptDetails
} from './pcr-score-card-form-interview-details'
import { scoreCardSchema } from './utils'

type PcrScoreCardFormProps = {
  className?: string
  staffApplication: StaffApplicationLight
  readOnly?: boolean
  defaultTab?: number

  defaultValues?: Partial<ScoreCardInput>
  isLoading?: boolean

  hasViewButton?: boolean

  handleSubmit?: (values: ScoreCardInput) => void
  handleReset?: () => void
}

export function PcrScoreCardForm({
  className,
  defaultValues,
  defaultTab = 0,
  isLoading,
  staffApplication,
  readOnly,
  hasViewButton = true,
  handleReset,
  handleSubmit
}: PcrScoreCardFormProps) {
  const queryClient = useQueryClient()
  const [selectedTab, setSelectedTab] = useState<{
    activeTab: number
    defaultTab: number
  }>({ activeTab: defaultTab, defaultTab })
  const { navigate } = useNavigator()
  const { t } = useTranslate()

  const form = useForm<ScoreCardInput>({
    resolver: zodResolver(scoreCardSchema(t)),
    defaultValues: {
      ...defaultValues,
      email: staffApplication.email,
      phoneNumber: staffApplication.phoneNumber
    },
    mode: FORM_MODE
  })

  const onSubmit = (values: ScoreCardInput) => {
    handleSubmit?.(values)

    // invalidate the staff-application-kanban query so that we get the updated data
    queryClient.invalidateQueries({
      predicate: (query) => query.queryKey.includes('staff-application-kanban')
    })
  }

  const { submitCount, errors } = form.formState

  // the candidate e-mail and phone are only rendered on the first tab: show it when they block the submit
  useEffect(() => {
    if (submitCount && (errors.email || errors.phoneNumber)) {
      setSelectedTab({ activeTab: 0, defaultTab })
    }
  }, [submitCount])

  const scoreCardEnteredFieldsStatus = getScoreCardEnteredFieldsStatus(form.watch())

  const activeTab = useMemo(() => {
    if (selectedTab.defaultTab !== defaultTab) {
      return defaultTab
    }

    return selectedTab.activeTab
  }, [defaultTab, selectedTab])

  return (
    <Form {...form}>
      <form onSubmit={form.handleSubmit(onSubmit)}>
        <LineTab
          activeTab={activeTab}
          onTabChange={(index) => setSelectedTab({ activeTab: index, defaultTab })}
          tabs={[
            { label: 'Détails du candidat' },
            { label: "Date de l'entretien" },
            { label: 'Evaluation' },
            { label: "Détails de l'entretien" }
          ]}
        />

        <div className={'h-4'} />

        <div className={cn('max-h-[50vh] overflow-y-auto pr-2')}>
          <div
            className={cn(
              'flex flex-col gap-5',
              {
                'pointer-events-none': readOnly
              },
              className
            )}
          >
            {/* candidate details */}
            {activeTab === 0 && (
              <>
                <PcrScoreCardFormCandidateDetails staffApplication={staffApplication} disabled />
                <DateAndMethodOfContact />
              </>
            )}

            {/* interview details */}
            {activeTab === 1 && <PcrScoreCardFormInterviewSchedule />}

            {/* evaluation */}
            {activeTab === 2 && (
              <PcrScoreCardFormEvaluation
                disabled={
                  scoreCardEnteredFieldsStatus !==
                    ScoreCardEnteredFieldsStatusEnum.ENTERED_SCHEDULED_INTERVIEW &&
                  scoreCardEnteredFieldsStatus !==
                    ScoreCardEnteredFieldsStatusEnum.ENTERED_EVALUATIONS &&
                  scoreCardEnteredFieldsStatus !==
                    ScoreCardEnteredFieldsStatusEnum.ENTERED_INTERVIEW_DETAILS
                }
              />
            )}

            {/* evaluation */}
            {activeTab === 3 && (
              <PcrScoreCardFormInterviewTranscriptDetails
                disabled={
                  scoreCardEnteredFieldsStatus !==
                    ScoreCardEnteredFieldsStatusEnum.ENTERED_EVALUATIONS &&
                  scoreCardEnteredFieldsStatus !==
                    ScoreCardEnteredFieldsStatusEnum.ENTERED_INTERVIEW_DETAILS
                }
                staffApplication={staffApplication}
              />
            )}
          </div>

          <ValidationButtons
            hasBorderTop
            buttonsConfig={modalButtonConfig({
              isLoading,
              disabled: readOnly,
              onConfirm: onSubmit
            })}
            preComponent={
              <>
                <Button
                  type={'button'}
                  label={'Fermer'}
                  variant={'ghost'}
                  disabled={isLoading}
                  onClick={handleReset}
                />
                {hasViewButton && (
                  <Button
                    type={'button'}
                    label={'Voir la candidature'}
                    variant={'ghost'}
                    disabled={isLoading}
                    icon={EyeOpenIcon}
                    onClick={() =>
                      navigate(
                        Paths.STAFF_APPLICATION_VIEW_PAGE.replace(
                          ':id',
                          staffApplication.id.toString()
                        )
                      )
                    }
                  />
                )}
              </>
            }
          />
        </div>
      </form>
    </Form>
  )
}

type PcrScoreCardModalProps = PcrScoreCardFormProps & {
  status: StaffApplicationStatusEnum | 'PAID'
  modalLoading?: boolean
  isOpen: boolean
  defaultTab?: number
  hasViewButton?: boolean
  onCancel: () => void
}

export const PcrScoreCardModal = (props: PcrScoreCardModalProps) => {
  return (
    <Modal
      isOpen={props.isOpen}
      onCancel={props.onCancel}
      title={'Détails de la candidature'}
      icon={NotebookIcon}
      headerChildren={
        <Badge
          variant={
            props.status !== 'PAID'
              ? staffApplicationStatusEnumMapping[props.status].variant
              : 'purple'
          }
          text={
            props.status !== 'PAID'
              ? staffApplicationStatusEnumMapping[props.status].label
              : 'Payé'
          }
        />
      }
      size="medium"
      hideActions
    >
      {props.modalLoading ? (
        <Loader center />
      ) : (
        <PcrScoreCardForm className={MODAL_BODY_CLASSNAME} {...props} />
      )}
    </Modal>
  )
}
